<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { normalizeDialogTextForPreview } from '@/features/dialog-text/wrap'
import { getCachedImageResource } from '@/features/preview/imageResourceCache'
import { buildPreviewImageClipVertices } from '@/features/preview/imageGeometry'
import {
  buildPreviewSceneState,
  type PreviewSceneImageChoiceState,
  type PreviewSceneImageState,
  type PreviewSceneState,
  type PreviewSceneChoiceState,
  type PreviewSceneTextState,
} from '@/features/preview/previewSceneBuilder'
import {
  getPreviewCenterOffset,
  getPreviewTextBaselineY,
  measurePreviewText,
} from '@/features/preview/textLayout'
import {
  drawTalkBitmapText,
  measureTalkRunWidth,
  resolveTalkBitmapMetrics,
  type TalkBitmapMetrics,
} from '@/features/dialog-text/talkBitmap'
import { ensureProjectFontLoaded } from '@/features/project-font/runtime'
import { useWorkspaceStore } from '@/store/workspace'
import type CustomUIData from '@/model/CustomUI/CustomUIData'
import CustomUIItem from '@/model/CustomUI/CustomUIItem'
import type EventBase from '@/model/EventClass/EventBase'
import type MainProject from '@/model/MainProject'

const props = defineProps<{
  events: EventBase[]
  selectedIndex: number | null | undefined
  revision: number
  resourceSignature?: string
  designWidth: number
  designHeight: number
  stageWidth: number
  stageHeight: number
  project: MainProject | null
  customUiData?: CustomUIData | null
  customUiRevision?: number
  resolveGraphicResourceUrl: (relativePath: string) => string
  resolveUiGraphicResourceUrl: (storedPath: string) => string
  resolveOtherGraphicResourceUrl?: (storedPath: string) => string
  resolveImageOptionGraphicResourceUrl: (storedPath: string) => string
}>()

const workspace = useWorkspaceStore()

type TextureRecord = {
  texture: WebGLTexture
  width: number
  height: number
}

type Drawable = {
  texture: WebGLTexture
  x: number
  y: number
  width: number
  height: number
  opacity: number
  rotation: number
  rotationPivot: 'center' | 'top-left' | 'custom'
  rotationPoint: { x: number; y: number } | null
  isMirror: boolean
}

type PreviewTextToken = {
  text: string
  color: string
}

const DEFAULT_DIALOG_MESSAGE_COLOR = '222, 226, 241'
const DEFAULT_DIALOG_UI_COLOR = '222, 226, 241'
const displayCanvasFrontRef = ref<HTMLCanvasElement | null>(null)
const glCanvasRef = ref<HTMLCanvasElement | null>(null)

let gl: WebGLRenderingContext | null = null
let program: WebGLProgram | null = null
let positionBuffer: WebGLBuffer | null = null
let texcoordBuffer: WebGLBuffer | null = null
let aPositionLocation = -1
let aTexcoordLocation = -1
let uOpacityLocation: WebGLUniformLocation | null = null
let uTextureLocation: WebGLUniformLocation | null = null
let renderVersion = 0
let scheduledRenderFrame = 0
let scheduledRenderTimer: ReturnType<typeof setTimeout> | null = null
let resolvedFontFamily = ''
let fontFamilyPromise: Promise<string> | null = null
let lastProjectFontName = ''
let lastFontRoot: unknown = null
const textureCache = new Map<string, TextureRecord>()
const DIALOG_CONTENT_LINE_HEIGHT_RATIO = 1.5

function reportPreviewError(error: unknown, context: string) {
  const detail = error instanceof Error ? error : new Error(String(error))
  throw detail
}

async function ensureFontFamily(): Promise<string> {
  if (resolvedFontFamily) return resolvedFontFamily
  if (!fontFamilyPromise) {
    fontFamilyPromise = ensureProjectFontLoaded(props.project, workspace.virtualResourceRoots.font)
      .then((family) => family || '"Microsoft YaHei", sans-serif')
      .then((family) => {
        resolvedFontFamily = family
        return family
      })
      .finally(() => {
        fontFamilyPromise = null
      })
  }
  return fontFamilyPromise
}

function createShader(context: WebGLRenderingContext, type: number, source: string): WebGLShader {
  const shader = context.createShader(type)
  if (!shader) {
    throw new Error('webgl shader create failed')
  }
  context.shaderSource(shader, source)
  context.compileShader(shader)
  if (!context.getShaderParameter(shader, context.COMPILE_STATUS)) {
    const message = context.getShaderInfoLog(shader) || 'webgl shader compile failed'
    context.deleteShader(shader)
    throw new Error(message)
  }
  return shader
}

function createProgram(context: WebGLRenderingContext): WebGLProgram {
  const vertexShader = createShader(
    context,
    context.VERTEX_SHADER,
    `
attribute vec2 a_position;
attribute vec2 a_texcoord;
varying vec2 v_texcoord;

void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
  v_texcoord = a_texcoord;
}
`,
  )
  const fragmentShader = createShader(
    context,
    context.FRAGMENT_SHADER,
    `
precision mediump float;
uniform sampler2D u_texture;
uniform float u_opacity;
varying vec2 v_texcoord;

void main() {
  vec4 color = texture2D(u_texture, v_texcoord);
  gl_FragColor = vec4(color.rgb * u_opacity, color.a * u_opacity);
}
`,
  )
  const nextProgram = context.createProgram()
  if (!nextProgram) {
    context.deleteShader(vertexShader)
    context.deleteShader(fragmentShader)
    throw new Error('webgl program create failed')
  }
  context.attachShader(nextProgram, vertexShader)
  context.attachShader(nextProgram, fragmentShader)
  context.linkProgram(nextProgram)
  context.deleteShader(vertexShader)
  context.deleteShader(fragmentShader)
  if (!context.getProgramParameter(nextProgram, context.LINK_STATUS)) {
    const message = context.getProgramInfoLog(nextProgram) || 'webgl program link failed'
    context.deleteProgram(nextProgram)
    throw new Error(message)
  }
  return nextProgram
}

function initWebGL() {
  const canvas = glCanvasRef.value
  if (!canvas) return
  gl = canvas.getContext('webgl', { alpha: true, antialias: true, premultipliedAlpha: true })
  if (!gl) return
  program = createProgram(gl)
  positionBuffer = gl.createBuffer()
  texcoordBuffer = gl.createBuffer()
  aPositionLocation = gl.getAttribLocation(program, 'a_position')
  aTexcoordLocation = gl.getAttribLocation(program, 'a_texcoord')
  uOpacityLocation = gl.getUniformLocation(program, 'u_opacity')
  uTextureLocation = gl.getUniformLocation(program, 'u_texture')
  gl.useProgram(program)
  gl.enable(gl.BLEND)
  gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA)
  gl.disable(gl.DEPTH_TEST)
}

function clearStage() {
  const canvas = glCanvasRef.value
  if (!gl || !canvas) return
  gl.viewport(0, 0, canvas.width, canvas.height)
  gl.clearColor(0, 0, 0, 1)
  gl.clear(gl.COLOR_BUFFER_BIT)
}

function syncCanvasSize() {
  const displayFrontCanvas = displayCanvasFrontRef.value
  const glCanvas = glCanvasRef.value
  if (!displayFrontCanvas || !glCanvas) return
  const dpr = window.devicePixelRatio || 1
  const pixelWidth = Math.max(1, Math.round(props.stageWidth * dpr))
  const pixelHeight = Math.max(1, Math.round(props.stageHeight * dpr))

  for (const canvas of [displayFrontCanvas, glCanvas]) {
    canvas.style.width = `${props.stageWidth}px`
    canvas.style.height = `${props.stageHeight}px`
    canvas.width = pixelWidth
    canvas.height = pixelHeight
  }
}

function presentStage() {
  const displayCanvas = displayCanvasFrontRef.value
  const glCanvas = glCanvasRef.value
  if (!displayCanvas || !glCanvas) return
  const ctx = displayCanvas.getContext('2d')
  if (!ctx) return
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.clearRect(0, 0, displayCanvas.width, displayCanvas.height)
  ctx.drawImage(glCanvas, 0, 0)
}

function toRgbCss(input: string, fallback = DEFAULT_DIALOG_MESSAGE_COLOR): string {
  const raw = String(input || '').trim()
  if (!raw) return `rgb(${fallback})`
  if (/^#[0-9a-f]{6}$/i.test(raw)) return raw
  const match = raw.match(/^(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})$/)
  if (!match) return `rgb(${fallback})`
  const channels = match.slice(1).map((item) => Math.max(0, Math.min(255, Number(item) || 0)))
  return `rgb(${channels.join(', ')})`
}

function uploadTexture(key: string, source: TexImageSource, width: number, height: number): TextureRecord {
  if (!gl) {
    throw new Error('webgl context is not initialized')
  }
  const cached = textureCache.get(key)
  if (cached) return cached
  const texture = gl.createTexture()
  if (!texture) {
    throw new Error(`webgl texture create failed: ${key}`)
  }
  gl.bindTexture(gl.TEXTURE_2D, texture)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, 1)
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source)
  const record = { texture, width, height }
  textureCache.set(key, record)
  return record
}

async function getImageTexture(url: string): Promise<TextureRecord> {
  const target = String(url || '').trim()
  if (!target) {
    throw new Error('image texture url is empty')
  }
  if (textureCache.has(target)) return textureCache.get(target)!
  const image = await getCachedImageResource(target)
  return uploadTexture(target, image, image.naturalWidth || image.width, image.naturalHeight || image.height)
}

async function getOptionalImageTexture(url: string): Promise<TextureRecord | null> {
  const target = String(url || '').trim()
  if (!target) return null
  try {
    return await getImageTexture(target)
  } catch {
    return null
  }
}

function normalizePreviewLineBreaks(text: string): string {
  return normalizeDialogTextForPreview(text)
}

function clampColorChannel(value: string): number {
  const next = Number.parseInt(value, 10)
  if (!Number.isFinite(next)) return 0
  return Math.max(0, Math.min(255, next))
}

function parsePreviewTextTokens(text: string, defaultColor: string): PreviewTextToken[] {
  const tokens: PreviewTextToken[] = []
  const colorTagPattern = /\\c\[\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\]/g
  let color = defaultColor
  let cursor = 0
  let hasColorTag = false
  for (const match of text.matchAll(colorTagPattern)) {
    hasColorTag = true
    const index = match.index ?? 0
    if (index > cursor) {
      tokens.push({ text: text.slice(cursor, index), color })
    }
    color = `rgb(${clampColorChannel(match[1]!)}, ${clampColorChannel(match[2]!)}, ${clampColorChannel(match[3]!)})`
    cursor = index + match[0].length
  }
  if (cursor < text.length) {
    tokens.push({ text: text.slice(cursor), color })
  }
  if (hasColorTag && !tokens.length) {
    return [{ text: '', color }]
  }
  return tokens.length ? tokens : [{ text, color }]
}

function pushTextLine(lines: PreviewTextToken[][], line: PreviewTextToken[]): void {
  lines.push(line.length ? line : [{ text: '', color: '' }])
}

function appendTextChar(line: PreviewTextToken[], char: string, color: string): void {
  const last = line[line.length - 1]
  if (last && last.color === color) {
    last.text += char
    return
  }
  line.push({ text: char, color })
}

function getLineText(line: PreviewTextToken[]): string {
  return line.map((token) => token.text).join('')
}

function buildPreviewTextLines(
  text: string,
  defaultColor: string,
): PreviewTextToken[][] {
  const lines: PreviewTextToken[][] = []
  let line: PreviewTextToken[] = []
  for (const token of parsePreviewTextTokens(text, defaultColor)) {
    for (const char of Array.from(token.text)) {
      if (char === '\n') {
        pushTextLine(lines, line)
        line = []
        continue
      }
      appendTextChar(line, char, token.color)
    }
  }
  if (line.length || !lines.length) {
    pushTextLine(lines, line)
  }
  return lines
}

function makeTextCanvas(
  key: string,
  text: string,
  width: number,
  height: number,
  color: string,
  fontSize: number,
  align: 'left' | 'center',
  singleLine: boolean,
  verticalAlign: 'top' | 'center',
  fontFamily: string,
  lineHeightRatio = 1.2,
): TextureRecord {
  if (textureCache.has(key)) return textureCache.get(key)!
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(width))
  canvas.height = Math.max(1, Math.round(height))
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error(`2d canvas context create failed: ${key}`)
  }

  const normalizedText = normalizePreviewLineBreaks(text)
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  if (!normalizedText) {
    return uploadTexture(key, canvas, canvas.width, canvas.height)
  }

  const nextFontSize = Math.max(10, Math.round(fontSize))
  ctx.textBaseline = 'alphabetic'
  ctx.textAlign = 'left'
  ctx.fillStyle = color
  ctx.font = `${nextFontSize}px ${fontFamily}`

  const maxWidth = canvas.width
  const maxHeight = canvas.height
  const lineHeight = Math.max(1, Math.round(nextFontSize * lineHeightRatio))
  const lines = singleLine
    ? [parsePreviewTextTokens(normalizedText.split('\n')[0] ?? normalizedText, color)]
    : buildPreviewTextLines(normalizedText, color)

  const primaryMetrics = measurePreviewText(ctx, getLineText(lines[0] ?? []), nextFontSize)
  let drawY = getPreviewTextBaselineY(
    maxHeight,
    primaryMetrics,
    singleLine ? verticalAlign : 'top',
  )
  for (const line of lines) {
    const lineText = getLineText(line)
    const metrics = measurePreviewText(ctx, lineText, nextFontSize)
    const lineTop = Math.round(drawY - metrics.ascent)
    if (!singleLine && lineTop >= maxHeight) break
    let drawX = align === 'center' ? Math.round((maxWidth - ctx.measureText(lineText).width) / 2) : 0
    for (const token of line) {
      if (!token.text) continue
      ctx.fillStyle = token.color || color
      ctx.fillText(token.text, drawX, drawY)
      drawX += ctx.measureText(token.text).width
    }
    if (singleLine) break
    drawY += lineHeight
  }

  return uploadTexture(key, canvas, canvas.width, canvas.height)
}

async function makeTalkBitmapTexture(
  key: string,
  text: string,
  color: string,
  metrics: TalkBitmapMetrics,
  singleLine: boolean,
): Promise<TextureRecord> {
  if (textureCache.has(key)) return textureCache.get(key)!
  const drawn = await drawTalkBitmapText({
    text,
    defaultColor: color,
    metrics,
    singleLine,
  })
  return uploadTexture(key, drawn.canvas, drawn.width, drawn.height)
}

function makeDefaultButtonCanvas(key: string): TextureRecord {
  if (textureCache.has(key)) return textureCache.get(key)!
  const canvas = document.createElement('canvas')
  canvas.width = 120
  canvas.height = 42
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error(`2d canvas context create failed: ${key}`)
  }
  ctx.fillStyle = '#1f2937'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.strokeStyle = '#93c5fd'
  ctx.lineWidth = 2
  ctx.strokeRect(1, 1, canvas.width - 2, canvas.height - 2)
  ctx.fillStyle = '#dbeafe'
  ctx.font = '18px "Microsoft YaHei", sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('按钮', canvas.width / 2, canvas.height / 2)
  return uploadTexture(key, canvas, canvas.width, canvas.height)
}

function buildPositionVertices(
  drawable: Drawable,
  scale: number,
  stageWidth: number,
  stageHeight: number,
): Float32Array {
  return buildPreviewImageClipVertices(
    {
      x: drawable.x,
      y: drawable.y,
      width: drawable.width,
      height: drawable.height,
      rotation: drawable.rotation,
      rotationPivot: drawable.rotationPivot,
      rotationPoint: drawable.rotationPoint,
      isMirror: drawable.isMirror,
    },
    scale,
    stageWidth,
    stageHeight,
  )
}

function drawDrawable(drawable: Drawable) {
  if (
    !gl
    || !program
    || !positionBuffer
    || !texcoordBuffer
    || aPositionLocation < 0
    || aTexcoordLocation < 0
  ) {
    return
  }
  const scale = Math.min(
    props.stageWidth / Math.max(1, props.designWidth),
    props.stageHeight / Math.max(1, props.designHeight),
  )
  const positions = buildPositionVertices(drawable, scale, props.stageWidth, props.stageHeight)
  const texcoords = new Float32Array([
    0, 0,
    1, 0,
    0, 1,
    1, 1,
  ])

  gl.useProgram(program)
  gl.bindTexture(gl.TEXTURE_2D, drawable.texture)
  gl.uniform1f(uOpacityLocation, drawable.opacity)
  gl.uniform1i(uTextureLocation, 0)

  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer)
  gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STREAM_DRAW)
  gl.enableVertexAttribArray(aPositionLocation)
  gl.vertexAttribPointer(aPositionLocation, 2, gl.FLOAT, false, 0, 0)

  gl.bindBuffer(gl.ARRAY_BUFFER, texcoordBuffer)
  gl.bufferData(gl.ARRAY_BUFFER, texcoords, gl.STREAM_DRAW)
  gl.enableVertexAttribArray(aTexcoordLocation)
  gl.vertexAttribPointer(aTexcoordLocation, 2, gl.FLOAT, false, 0, 0)

  gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
}

async function buildImageDrawables(images: PreviewSceneImageState[]): Promise<Drawable[]> {
  const out: Drawable[] = []
  for (const image of images) {
    const graphicUrl = props.resolveGraphicResourceUrl(image.path)
    if (!String(image.path || '').trim() || !String(graphicUrl || '').trim()) {
      continue
    }
    let texture: TextureRecord
    try {
      texture = await getImageTexture(graphicUrl)
    } catch {
      continue
    }
    out.push({
      texture: texture.texture,
      x: image.x,
      y: image.y,
      width: texture.width * (image.zoomX / 100),
      height: texture.height * (image.zoomY / 100),
      opacity: image.opacity / 255,
      rotation: image.rotation,
      rotationPivot: image.rotationPivot,
      rotationPoint: image.rotationPoint,
      isMirror: image.isMirror,
    })
  }
  return out
}

async function buildDialogDrawables(
  textState: PreviewSceneTextState,
  fontFamily: string,
): Promise<Drawable[]> {
  const drawables: Drawable[] = []
  const designWidth = props.designWidth
  const designHeight = props.designHeight
  const talkSetting = props.project?.systemData?.Talk
  const totalSetting = props.project?.systemData?.totalSetting
  const talkWin = talkSetting?.TalkWin
  const nameWin = talkSetting?.NameWinS
  const roleName = textState.roleName
  const content = textState.content
  const portraitUrl = props.resolveGraphicResourceUrl(textState.portraitPath)
  const portraitPos = textState.portraitPos
  const showDialog = textState.showDialog
  const messageColor = toRgbCss(totalSetting?.FontColor_MSG ?? DEFAULT_DIALOG_MESSAGE_COLOR)
  const roleColor = toRgbCss(textState.roleColor, totalSetting?.FontColor_MSG ?? DEFAULT_DIALOG_MESSAGE_COLOR)
  const talkBoxUrl = props.resolveUiGraphicResourceUrl(talkWin?.BackPic?.name ?? '')
  const talkBoxX = Number(talkWin?.X ?? 0)
  const talkBoxBottom = Number(talkWin?.Y ?? 0)
  const talkTextOffsetX = Number(talkWin?.p_x1 ?? 0)
  const talkTextOffsetY = Number(talkWin?.p_y1 ?? 0)
  const nameBoxUrl = props.resolveUiGraphicResourceUrl(nameWin?.BackPic?.name ?? '')
  const nameBoxOffsetX = Number(nameWin?.x ?? 0)
  const nameBoxOffsetY = Number(nameWin?.y ?? 0)
  const nameTextOffsetX = Number(nameWin?.P_x ?? 0)
  const nameTextOffsetY = Number(nameWin?.P_y ?? 0)
  const nameCenter = Boolean(nameWin?.IsCenter)
  const talkMetrics = resolveTalkBitmapMetrics(
    props.project,
    workspace.virtualResourceRoots.font,
    fontFamily,
  )

  if (portraitUrl) {
    const portrait = await getImageTexture(portraitUrl)
    const maxWidth = designWidth * 0.32
    const maxHeight = designHeight * 0.72
    const fitScale = Math.min(maxWidth / portrait.width, maxHeight / portrait.height)
    const drawWidth = Math.max(1, portrait.width * fitScale)
    const drawHeight = Math.max(1, portrait.height * fitScale)
    const bottomOffset = 96
    const horizontalOffset = 16
    const x = portraitPos === 1
      ? Math.max(0, designWidth - horizontalOffset - drawWidth)
      : horizontalOffset
    const y = Math.max(0, designHeight - bottomOffset - drawHeight)
    drawables.push({
      texture: portrait.texture,
      x,
      y,
      width: drawWidth,
      height: drawHeight,
      opacity: 1,
      rotation: 0,
      rotationPivot: 'top-left',
      rotationPoint: null,
      isMirror: false,
    })
  }

  if ((content.length > 0 || roleName.trim()) && !String(talkBoxUrl || '').trim()) {
    throw new Error('dialog talk box texture url is empty')
  }
  if (roleName.trim() && !String(nameBoxUrl || '').trim()) {
    throw new Error('dialog name box texture url is empty')
  }

  const talkTexture = (content.length > 0 || roleName.trim())
    ? await getImageTexture(talkBoxUrl)
    : null
  const nameTexture = roleName.trim()
    ? await getImageTexture(nameBoxUrl)
    : null

  const talkWidth = talkTexture?.width ?? 720
  const talkHeight = talkTexture?.height ?? 180
  const talkX = talkBoxX
  const talkY = designHeight - talkBoxBottom - talkHeight

  if (showDialog && talkTexture) {
    drawables.push({
      texture: talkTexture.texture,
      x: talkX,
      y: talkY,
      width: talkWidth,
      height: talkHeight,
      opacity: 1,
      rotation: 0,
      rotationPivot: 'top-left',
      rotationPoint: null,
      isMirror: false,
    })
  }

  const nameX = talkX + nameBoxOffsetX
  const nameY = talkY + nameBoxOffsetY
  const nameWidth = nameTexture?.width ?? 180
  const nameHeight = nameTexture?.height ?? 60
  const nameTextWidth = roleName.trim() && talkMetrics
    ? measureTalkRunWidth(roleName, talkMetrics)
    : 0
  const nameTextHeight = talkMetrics?.cellHeight ?? 0

  if (roleName.trim() && showDialog && nameTexture) {
    drawables.push({
      texture: nameTexture.texture,
      x: nameX,
      y: nameY,
      width: nameWidth,
      height: nameHeight,
      opacity: 1,
      rotation: 0,
      rotationPivot: 'top-left',
      rotationPoint: null,
      isMirror: false,
    })
  }

  if (roleName.trim() && talkMetrics) {
    const nameTextX = nameCenter
      ? nameX + getPreviewCenterOffset(nameWidth, nameTextWidth)
      : nameX + nameTextOffsetX
    const nameTextY = nameCenter
      ? nameY + getPreviewCenterOffset(nameHeight, nameTextHeight)
      : nameY + nameTextOffsetY
    const nameTextureCanvas = await makeTalkBitmapTexture(
      ['name-bitmap', roleName, roleColor, talkMetrics.metricKey].join('|'),
      roleName,
      roleColor || 'rgb(255, 255, 255)',
      talkMetrics,
      true,
    )
    drawables.push({
      texture: nameTextureCanvas.texture,
      x: nameTextX,
      y: nameTextY,
      width: nameTextureCanvas.width,
      height: nameTextureCanvas.height,
      opacity: 1,
      rotation: 0,
      rotationPivot: 'top-left',
      rotationPoint: null,
      isMirror: false,
    })
  }

  if (content.length > 0 && talkMetrics) {
    const previewContent = normalizePreviewLineBreaks(content)
    const contentTexture = await makeTalkBitmapTexture(
      ['content-bitmap', previewContent, messageColor, talkMetrics.metricKey].join('|'),
      previewContent,
      messageColor || 'rgb(222, 226, 241)',
      talkMetrics,
      false,
    )
    drawables.push({
      texture: contentTexture.texture,
      x: talkX + talkTextOffsetX,
      y: talkY + talkTextOffsetY,
      width: contentTexture.width,
      height: contentTexture.height,
      opacity: 1,
      rotation: 0,
      rotationPivot: 'top-left',
      rotationPoint: null,
      isMirror: false,
    })
  }

  return drawables
}

/**
 * 引擎 CTextChoice：有对话框时可用高度 = 游戏高 - (对话框图高 + Talk.Y)。
 */
async function resolveTextChoiceStackOffsetY(
  totalH: number,
  textState: PreviewSceneTextState | null,
): Promise<number> {
  let messageHeight = 0
  if (textState) {
    const talkWin = props.project?.systemData?.Talk?.TalkWin
    const talkBoxUrl = props.resolveUiGraphicResourceUrl(talkWin?.BackPic?.name ?? '')
    if (String(talkBoxUrl || '').trim()) {
      const talkTexture = await getImageTexture(talkBoxUrl)
      messageHeight = talkTexture.height + Number(talkWin?.Y ?? 0)
      if (props.designHeight > props.designWidth) {
        const nameBackY = Number(props.project?.systemData?.Talk?.NameWinS?.y ?? 0)
        if (nameBackY < 0) messageHeight -= nameBackY
      }
    }
  }
  const { designWidth, designHeight } = props
  if (designHeight > designWidth) {
    const ratio = messageHeight > 0 ? 0.025 : 0.05
    return (1 - ratio) * designHeight - messageHeight - totalH
  }
  return (designHeight - messageHeight - totalH) / 2
}

async function buildTextChoiceDrawables(
  choiceState: PreviewSceneChoiceState,
  fontFamily: string,
  textState: PreviewSceneTextState | null,
): Promise<Drawable[]> {
  const drawables: Drawable[] = []
  const talkSetting = props.project?.systemData?.Talk
  const totalSetting = props.project?.systemData?.totalSetting
  const items = choiceState.items
  if (!items.length) return drawables
  const uiColor = toRgbCss(totalSetting?.FontColor_UI ?? DEFAULT_DIALOG_UI_COLOR)
  const talkMetrics = resolveTalkBitmapMetrics(
    props.project,
    workspace.virtualResourceRoots.font,
    fontFamily,
  )
  const defaultButtonIndex = Number(talkSetting?.SelectButton ?? -1)
  const buttons = props.project?.systemData?.btnList?.buttons ?? []
  const textureByIndex = new Map<number, TextureRecord>()
  for (const item of items) {
    const buttonIndex = item.buttonIndex ?? defaultButtonIndex
    if (!Number.isFinite(buttonIndex) || buttonIndex < 0 || textureByIndex.has(buttonIndex)) continue
    const button = buttons[buttonIndex] ?? null
    const buttonPath = String(button?.NoneImage?.name ?? '').trim()
    if (!buttonPath) continue
    const texture = await getOptionalImageTexture(props.resolveImageOptionGraphicResourceUrl(buttonPath))
    if (texture) textureByIndex.set(buttonIndex, texture)
  }
  if (!textureByIndex.size) return drawables
  const gap = 10
  const originY = choiceState.originY
  let offsetY = 0
  if (originY === null && items.some((item) => item.y === null)) {
    let totalH = 0
    for (const item of items) {
      const buttonTexture = textureByIndex.get(item.buttonIndex ?? defaultButtonIndex)
      if (!buttonTexture) continue
      if (totalH > 0) totalH += gap
      totalH += Math.max(1, buttonTexture.height)
    }
    offsetY = await resolveTextChoiceStackOffsetY(Math.max(1, totalH), textState)
  }

  for (let index = 0; index < items.length; index += 1) {
    const item = items[index]!
    const content = String(item.text ?? '')
    const buttonIndex = item.buttonIndex ?? defaultButtonIndex
    const buttonTexture = textureByIndex.get(buttonIndex)
    if (!buttonTexture) continue
    const itemWidth = Math.max(1, buttonTexture.width)
    const itemHeight = Math.max(1, buttonTexture.height)
    const pinned = item.x !== null && item.y !== null
    const x = pinned
      ? item.x!
      : (choiceState.originX ?? Math.round((props.designWidth - itemWidth) / 2))
    let y: number
    if (pinned) {
      y = item.y!
    } else {
      y = originY === null ? offsetY : originY + offsetY
      offsetY += itemHeight + gap
    }
    const textX = item.isCenter ? 0 : item.textX
    const textY = item.isCenter ? 0 : item.textY
    drawables.push({
      texture: buttonTexture.texture,
      x,
      y,
      width: itemWidth,
      height: itemHeight,
      opacity: 1,
      rotation: 0,
      rotationPivot: 'top-left',
      rotationPoint: null,
      isMirror: false,
    })

    if (!content || !talkMetrics) continue
    const previewContent = normalizePreviewLineBreaks(content)
    const textTexture = await makeTalkBitmapTexture(
      ['choice-bitmap', index, previewContent, uiColor, talkMetrics.metricKey].join('|'),
      previewContent,
      uiColor || 'rgb(222, 226, 241)',
      talkMetrics,
      false,
    )
    const drawTextX = item.isCenter
      ? x + getPreviewCenterOffset(itemWidth, textTexture.width)
      : x + textX
    const drawTextY = item.isCenter
      ? y + getPreviewCenterOffset(itemHeight, textTexture.height)
      : y + textY
    drawables.push({
      texture: textTexture.texture,
      x: drawTextX,
      y: drawTextY,
      width: textTexture.width,
      height: textTexture.height,
      opacity: 1,
      rotation: 0,
      rotationPivot: 'top-left',
      rotationPoint: null,
      isMirror: false,
    })
  }

  return drawables
}

async function buildImageChoiceDrawables(
  choiceState: PreviewSceneImageChoiceState,
): Promise<Drawable[]> {
  const drawables: Drawable[] = []
  const buttons = props.project?.systemData?.btnList?.buttons ?? []
  for (const item of choiceState.items) {
    const button = buttons[item.buttonIndex] ?? null
    const buttonPath = String(button?.NoneImage?.name ?? button?.MoveImage?.name ?? '').trim()
    if (!buttonPath) continue
    const buttonUrl = props.resolveImageOptionGraphicResourceUrl(buttonPath)
    const buttonTexture = await getOptionalImageTexture(buttonUrl)
    if (!buttonTexture) continue
    drawables.push({
      texture: buttonTexture.texture,
      x: item.x,
      y: item.y,
      width: Math.max(1, buttonTexture.width),
      height: Math.max(1, buttonTexture.height),
      opacity: 1,
      rotation: 0,
      rotationPivot: 'top-left',
      rotationPoint: null,
      isMirror: false,
    })
  }
  return drawables
}

async function buildSceneDrawables(sceneState: PreviewSceneState, fontFamily: string): Promise<Drawable[]> {
  if (sceneState.kind !== 'scene') return []
  const imageDrawables = await buildImageDrawables(sceneState.images)
  const dialogDrawables = sceneState.text ? await buildDialogDrawables(sceneState.text, fontFamily) : []
  const imageChoiceDrawables = sceneState.imageChoices ? await buildImageChoiceDrawables(sceneState.imageChoices) : []
  const textChoiceDrawables = sceneState.textChoices
    ? await buildTextChoiceDrawables(sceneState.textChoices, fontFamily, sceneState.text)
    : []
  return [
    ...imageDrawables,
    ...dialogDrawables,
    ...imageChoiceDrawables,
    ...textChoiceDrawables,
  ]
}

function resolveCustomUiOtherGraphicUrl(path: string): string {
  return props.resolveOtherGraphicResourceUrl?.(path) ?? props.resolveGraphicResourceUrl(path)
}

async function buildCustomUiControlDrawables(items: CustomUIItem[], fontFamily: string): Promise<Drawable[]> {
  const drawables: Drawable[] = []
  const buttons = props.project?.systemData?.btnList?.buttons ?? []
  for (const item of items) {
    const x = item.IsUserVar ? 0 : Number(item.X || 0)
    const y = item.IsUserVar ? 0 : Number(item.Y || 0)
    if (item.Type === CustomUIItem.controlType.Button) {
      const button = buttons[Number(item.Index || 0)] ?? null
      const buttonPath = item.IsUserIndex ? '' : String(button?.NoneImage?.name ?? button?.MoveImage?.name ?? '').trim()
      const texture = buttonPath
        ? await getOptionalImageTexture(props.resolveImageOptionGraphicResourceUrl(buttonPath))
        : makeDefaultButtonCanvas('custom-ui-default-button')
      if (!texture) continue
      drawables.push({
        texture: texture.texture,
        x,
        y,
        width: Math.max(1, texture.width),
        height: Math.max(1, texture.height),
        opacity: 1,
        rotation: 0,
        rotationPivot: 'top-left',
        rotationPoint: null,
        isMirror: false,
      })
      continue
    }
    if (item.Type === CustomUIItem.controlType.Pic && !item.IsUserString && item.ImagePath1) {
      const texture = await getOptionalImageTexture(resolveCustomUiOtherGraphicUrl(item.ImagePath1))
      if (!texture) continue
      drawables.push({
        texture: texture.texture,
        x,
        y,
        width: Math.max(1, texture.width),
        height: Math.max(1, texture.height),
        opacity: 1,
        rotation: 0,
        rotationPivot: 'top-left',
        rotationPoint: null,
        isMirror: false,
      })
      continue
    }
    if (item.Type === CustomUIItem.controlType.String || item.Type === CustomUIItem.controlType.Var) {
      const text = item.Type === CustomUIItem.controlType.String ? `字符串 ${item.StringIndex}` : `数值 ${item.Index}`
      const texture = makeTextCanvas(
        ['custom-ui', item.Type, text, item.Color, item.StringIndex, item.Index, fontFamily].join('|'),
        text,
        160,
        36,
        toRgbCss(item.Color, '255, 255, 255'),
        24,
        'left',
        false,
        'top',
        fontFamily,
      )
      drawables.push({
        texture: texture.texture,
        x,
        y,
        width: 160,
        height: 36,
        opacity: 1,
        rotation: 0,
        rotationPivot: 'top-left',
        rotationPoint: null,
        isMirror: false,
      })
      continue
    }
    if (item.Type === CustomUIItem.controlType.Drap) {
      const baseTexture = item.ImagePath1 ? await getOptionalImageTexture(resolveCustomUiOtherGraphicUrl(item.ImagePath1)) : null
      if (baseTexture) {
        drawables.push({
          texture: baseTexture.texture,
          x,
          y,
          width: Math.max(1, baseTexture.width),
          height: Math.max(1, baseTexture.height),
          opacity: 1,
          rotation: 0,
          rotationPivot: 'top-left',
          rotationPoint: null,
          isMirror: false,
        })
      }
      const thumbTexture = item.ImagePath2 ? await getOptionalImageTexture(resolveCustomUiOtherGraphicUrl(item.ImagePath2)) : null
      if (!thumbTexture) continue
      drawables.push({
        texture: thumbTexture.texture,
        x,
        y,
        width: Math.max(1, thumbTexture.width),
        height: Math.max(1, thumbTexture.height),
        opacity: 1,
        rotation: 0,
        rotationPivot: 'top-left',
        rotationPoint: null,
        isMirror: false,
      })
    }
  }
  return drawables
}

async function renderScene() {
  const ticket = ++renderVersion
  syncCanvasSize()
  if (!gl) {
    return
  }
  const customUiData = props.customUiData ?? null
  const sceneState = customUiData ? null : buildPreviewSceneState(props.events, props.selectedIndex, props.revision)
  if (!customUiData && sceneState?.kind !== 'scene') {
    clearStage()
    presentStage()
    return
  }

  try {
    const fontFamily = await ensureFontFamily()
    if (ticket !== renderVersion) return

    const drawables = customUiData
      ? [
          ...await buildSceneDrawables(
            buildPreviewSceneState(customUiData.LoadEvent as unknown as EventBase[], Math.max(0, customUiData.LoadEvent.length - 1)),
            fontFamily,
          ),
          ...await buildCustomUiControlDrawables(customUiData.ControlList as CustomUIItem[], fontFamily),
          ...await buildSceneDrawables(
            buildPreviewSceneState(customUiData.AfterEvent as unknown as EventBase[], Math.max(0, customUiData.AfterEvent.length - 1)),
            fontFamily,
          ),
        ]
      : await buildSceneDrawables(sceneState!, fontFamily)
    if (ticket !== renderVersion) return

    clearStage()
    for (const drawable of drawables) {
      drawDrawable(drawable)
    }
    presentStage()
  } catch (error) {
    reportPreviewError(error, 'render scene failed')
  }
}

function scheduleRender(delayMs = 0) {
  if (scheduledRenderTimer !== null) {
    window.clearTimeout(scheduledRenderTimer)
    scheduledRenderTimer = null
  }
  if (scheduledRenderFrame) {
    cancelAnimationFrame(scheduledRenderFrame)
    scheduledRenderFrame = 0
  }
  if (delayMs > 0) {
    scheduledRenderTimer = window.setTimeout(() => {
      scheduledRenderTimer = null
      scheduleRender()
    }, delayMs)
    return
  }
  scheduledRenderFrame = requestAnimationFrame(() => {
    scheduledRenderFrame = 0
    void renderScene().catch((error) => reportPreviewError(error, 'scheduled render failed'))
  })
}

watch(
  () => [
    props.selectedIndex,
    props.revision,
    props.stageWidth,
    props.stageHeight,
    props.designWidth,
    props.designHeight,
    props.project,
    props.resourceSignature,
    props.customUiData,
    props.customUiRevision,
    props.project?.systemData?.totalSetting?.FontName,
    props.project?.systemData?.totalSetting?.FontSize,
    props.project?.systemData?.totalSetting?.FontColor_MSG,
    props.project?.systemData?.totalSetting?.FontColor_UI,
    workspace.virtualResourceRoots.font,
  ] as const,
  (next, previous) => {
    const nextFontName = String(props.project?.systemData?.totalSetting?.FontName ?? '')
    const nextFontRoot = workspace.virtualResourceRoots.font
    if (nextFontName !== lastProjectFontName || nextFontRoot !== lastFontRoot) {
      lastProjectFontName = nextFontName
      lastFontRoot = nextFontRoot
      resolvedFontFamily = ''
      fontFamilyPromise = null
    }
    const onlyRevisionChanged = Boolean(previous)
      && next[1] !== previous[1]
      && next.every((value, index) => index === 1 || value === previous[index])
    scheduleRender(onlyRevisionChanged ? 60 : 0)
  },
)

watch(
  () => props.project,
  () => {
    resolvedFontFamily = ''
    fontFamilyPromise = null
  },
)

onMounted(() => {
  initWebGL()
  scheduleRender()
})

onBeforeUnmount(() => {
  renderVersion += 1
  if (scheduledRenderFrame) {
    cancelAnimationFrame(scheduledRenderFrame)
    scheduledRenderFrame = 0
  }
  if (scheduledRenderTimer !== null) {
    window.clearTimeout(scheduledRenderTimer)
    scheduledRenderTimer = null
  }
  if (gl) {
    for (const record of textureCache.values()) {
      gl.deleteTexture(record.texture)
    }
    if (positionBuffer) gl.deleteBuffer(positionBuffer)
    if (texcoordBuffer) gl.deleteBuffer(texcoordBuffer)
    if (program) gl.deleteProgram(program)
  }
  textureCache.clear()
  resolvedFontFamily = ''
  fontFamilyPromise = null
  gl = null
  program = null
  positionBuffer = null
  texcoordBuffer = null
})
</script>

<template>
  <div class="preview-webgl-shell">
    <canvas
      ref="displayCanvasFrontRef"
      class="preview-game-canvas preview-game-canvas--webgl preview-game-canvas--display"
      aria-label="剧情 WebGL 预览"
    />
    <canvas
      ref="glCanvasRef"
      class="preview-game-canvas preview-game-canvas--webgl preview-game-canvas--buffer"
      aria-hidden="true"
    />
  </div>
</template>

<style scoped>
.preview-webgl-shell {
  position: absolute;
  inset: 0;
}

.preview-game-canvas--webgl {
  position: absolute;
  inset: 0;
}

.preview-game-canvas--display {
  pointer-events: none;
}

.preview-game-canvas--buffer {
  visibility: hidden;
  pointer-events: none;
}
</style>
